import React, { useRef, useState } from "react";
import { UploadCloud, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { uploadFile } from "@/lib/api";

const SLOTS = [
    { key: "front", label: "Front photo" },
    { key: "side", label: "Side photo" },
    { key: "back", label: "Back photo" },
    { key: "extra", label: "Additional (optional)" },
];

// value: { front: {path,url}, side: ... }
const PhotoUploader = ({ value = {}, onChange, disabled }) => {
    const [uploading, setUploading] = useState(null);

    const handleFile = async (slot, file) => {
        if (!file) return;
        setUploading(slot);
        try {
            const data = await uploadFile(file, "reference_photo");
            onChange({ ...value, [slot]: { path: data.path, previewUrl: URL.createObjectURL(file) } });
            toast.success(`${slot} photo uploaded`);
        } catch (e) {
            toast.error("Upload failed");
        } finally {
            setUploading(null);
        }
    };

    const remove = (slot) => {
        const next = { ...value };
        delete next[slot];
        onChange(next);
    };

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="photo-uploader">
            {SLOTS.map((s) => (
                <Slot key={s.key} slot={s} val={value[s.key]} uploading={uploading === s.key} onFile={(f) => handleFile(s.key, f)} onRemove={() => remove(s.key)} disabled={disabled} />
            ))}
        </div>
    );
};

const Slot = ({ slot, val, uploading, onFile, onRemove, disabled }) => {
    const ref = useRef();
    const [drag, setDrag] = useState(false);

    return (
        <div
            className={`relative aspect-[4/5] rounded-2xl border-2 border-dashed transition-all overflow-hidden ${
                val ? "border-primary/30 bg-white" : drag ? "border-primary bg-primary/5" : "border-border bg-white/40"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
            data-testid={`upload-slot-${slot.key}`}
        >
            <input
                ref={ref}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
                disabled={disabled}
            />
            {val ? (
                <>
                    {val.previewUrl && <img src={val.previewUrl} alt={slot.label} className="w-full h-full object-cover" />}
                    <div className="absolute top-2 right-2 flex gap-1">
                        <button onClick={onRemove} className="w-7 h-7 rounded-full bg-white/90 flex items-center justify-center text-destructive"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="absolute bottom-2 left-2 right-2 bg-white/85 backdrop-blur rounded-lg px-2 py-1 flex items-center gap-1 text-[11px] font-medium">
                        <Check className="w-3 h-3 text-emerald-600" /> {slot.label}
                    </div>
                </>
            ) : (
                <button
                    onClick={() => ref.current?.click()}
                    disabled={disabled || uploading}
                    className="absolute inset-0 flex flex-col items-center justify-center p-3 text-center"
                >
                    {uploading ? (
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-2 text-primary"><UploadCloud className="w-5 h-5" /></div>
                    )}
                    <span className="text-xs font-semibold">{slot.label}</span>
                    <span className="text-[10px] text-muted-foreground mt-0.5">Drop or click</span>
                </button>
            )}
        </div>
    );
};

export default PhotoUploader;
